/**
 * AppStatus - Atom component for displaying application status
 */
import React from 'react';

export type AppStatusType = 'active' | 'inactive' | 'syncing' | 'error' | 'unknown';

interface AppStatusProps {
  status: AppStatusType;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  showIcon?: boolean;
  className?: string;
}

const AppStatus: React.FC<AppStatusProps> = ({
  status,
  size = 'md',
  showLabel = true,
  showIcon = true,
  className = ''
}) => {
  const getStatusConfig = (status: AppStatusType) => {
    switch (status) {
      case 'active':
        return {
          label: 'Active',
          icon: '🟢',
          bgColor: 'bg-green-100 text-green-800 border-green-200',
          darkColor: 'dark:bg-green-900/20 dark:text-green-300 dark:border-green-800',
          dotColor: 'bg-green-500'
        };
      case 'inactive':
        return {
          label: 'Inactive',
          icon: '⚫',
          bgColor: 'bg-gray-100 text-gray-800 border-gray-200',
          darkColor: 'dark:bg-gray-900/20 dark:text-gray-300 dark:border-gray-800',
          dotColor: 'bg-gray-500'
        };
      case 'syncing':
        return {
          label: 'Syncing',
          icon: '🔄',
          bgColor: 'bg-yellow-100 text-yellow-800 border-yellow-200',
          darkColor: 'dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-800',
          dotColor: 'bg-yellow-500 animate-pulse'
        };
      case 'error':
        return {
          label: 'Error',
          icon: '🔴',
          bgColor: 'bg-red-100 text-red-800 border-red-200',
          darkColor: 'dark:bg-red-900/20 dark:text-red-300 dark:border-red-800',
          dotColor: 'bg-red-500'
        };
      case 'unknown':
      default:
        return {
          label: 'Unknown',
          icon: '❓',
          bgColor: 'bg-gray-100 text-gray-800 border-gray-200',
          darkColor: 'dark:bg-gray-900/20 dark:text-gray-300 dark:border-gray-800',
          dotColor: 'bg-gray-400'
        };
    }
  };

  const getSizeClasses = (size: string) => {
    switch (size) {
      case 'sm':
        return {
          container: 'px-2 py-1 text-xs',
          dot: 'w-2 h-2',
          icon: 'text-xs'
        };
      case 'lg':
        return {
          container: 'px-3 py-2 text-base',
          dot: 'w-4 h-4',
          icon: 'text-base'
        };
      case 'md':
      default:
        return {
          container: 'px-2 py-1 text-sm',
          dot: 'w-3 h-3',
          icon: 'text-sm'
        };
    }
  };

  const config = getStatusConfig(status);
  const sizeClasses = getSizeClasses(size);

  return (
    <span
      className={`
        inline-flex items-center gap-1 rounded-full border font-medium
        ${config.bgColor} ${config.darkColor}
        ${sizeClasses.container}
        ${className}
      `}
      role="status"
      aria-label={`Application status: ${config.label}`}
    >
      {showIcon && (
        <span 
          className={`${sizeClasses.icon}`}
          role="img"
          aria-label={config.label}
        >
          {config.icon}
        </span>
      )}
      
      {/* Alternative dot indicator */}
      {!showIcon && (
        <span 
          className={`${sizeClasses.dot} ${config.dotColor} rounded-full`}
          aria-hidden="true"
        />
      )}
      
      {showLabel && (
        <span>{config.label}</span>
      )}
    </span>
  );
};

export default AppStatus;